import React from "react";
import { Paper, Typography } from "@mui/material";
import { useClientAvailableSessionTypesQuery } from "@src/queries/sessionTypes";
import { ReactComponent as CheckIcon } from "@src/resources/icons/check.svg";
import { ReactComponent as CloseIcon } from "@src/resources/icons/close.svg";
import { ReactComponent as GroupIcon } from "@src/resources/icons/group_icon.svg";
import { SESSION_TYPE } from "@src/constants";
import { useTranslation } from "react-i18next";
import Spinner from "@openup/shared/components/Spinner/Spinner";

interface ClientAvailableSessionTypesProps {
  clientId: string;
}

const ClientAvailableSessionTypes: React.FC<
  ClientAvailableSessionTypesProps
> = ({ clientId }) => {
  const { t } = useTranslation();
  // Get all available session types (excluding medical)
  const allSessionTypes = () =>
    Object.values(SESSION_TYPE)
      .filter((value) => value !== SESSION_TYPE.MEDICAL)
      .map((value) => ({
        value,
        label: t(`SessionType.${value}`),
      }));

  // Get session types available for this specific client
  const { data: clientSessionTypes, isLoading: loadingClientTypes } =
    useClientAvailableSessionTypesQuery(clientId);

  if (loadingClientTypes) {
    return <Spinner />;
  }

  // Create a set of available session type names for quick lookup
  const availableSessionTypeNames = new Set(
    clientSessionTypes?.tags?.map((tag) => tag.name) || [],
  );

  return (
    <Paper elevation={0} className="p-6 mb-4">
      <div className="flex items-center mb-3.5">
        <GroupIcon className="w-6 h-6 text-gray-700 mr-3.5" />
        <Typography className="subtitle2 font-semibold">
          {t("ClientAvailableSessionType.Header")}
        </Typography>
      </div>

      {/* Vertical list of all session types */}
      <div className="space-y-3">
        {allSessionTypes().map(
          (sessionType: { value: string; label: string }) => {
            const isAvailable = availableSessionTypeNames.has(
              sessionType.value,
            );

            return (
              <div key={sessionType.value} className="flex items-center body2">
                <div className="w-5 h-5 flex-shrink-0 mr-4">
                  {isAvailable ? (
                    <CheckIcon className="w-full h-full text-green-500" />
                  ) : (
                    <CloseIcon className="w-full h-full text-red-500" />
                  )}
                </div>
                <Typography variant="body2" className="flex-1">
                  {sessionType.label}
                </Typography>
              </div>
            );
          },
        )}
      </div>
    </Paper>
  );
};

export default ClientAvailableSessionTypes;
